<template>
    <div @contextmenu.prevent="() => false">
        <l-map 
            ref="map" id="map"
            :style="'height:calc(100vh - ' + navHeight + 'px)'" 
            :options="{zoomControl: false}"
            :zoom="zoom"
            :center="center"
            :maxBounds="bounds"
            :minZoom="7"
            :maxZoom="maxZoom"
            @update:zoom="zoom = $event"
        >
            <l-tile-layer ref="tiles"
                @ready="$event.on('tileerror', tileErrorHandler)"
                :url="provider.url" 
                :attribution="attribution" />
            <uebersicht-control-hint 
                @hidden="showArrows"/>
            <uebersicht-control-bar>
                <uebersicht-control-filter 
                    v-model="angebote"/>
                <uebersicht-control-provider 
                    v-model="provider" />
                <uebersicht-control-areas
                    v-model="polyVisible"/>
            </uebersicht-control-bar>
            
            <uebersicht-control-zoom
                v-model="zoom"/>
            <uebersicht-control-center/>

            <uebersicht-marker-public
                ref="mp"/>
            <uebersicht-marker-space/>
            <uebersicht-marker-focus/>
            <uebersicht-marker-school 
                v-model="schulAdresse" />
            <uebersicht-marker-user 
                v-for="group in mapMarkerData"
                @zoom-in="$refs.map.mapObject.setView($event.latlng, $event.zoom)"
                @click="handleUserClick(group)"
                :key="group.id ? group.id : group.lat"
                :value="group" />
            <uebersicht-polygon-areas
                :visible="polyVisible" />
        </l-map>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { attribution, tileProviders } from '../config'
import { setAlert } from '../tools'
import { LMap, LTileLayer } from 'vue2-leaflet'
import "leaflet/dist/leaflet.css"
import { Icon /* ,Routing */, latLng, latLngBounds } from 'leaflet'
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

import UebersichtControlProvider from "../components/UebersichtControlProvider.vue"
import UebersichtControlHint from "../components/UebersichtControlHint.vue"
import UebersichtControlAreas from "../components/UebersichtControlAreas.vue"
import UebersichtPolygonAreas from "../components/UebersichtPolygonAreas.vue"
import UebersichtControlZoom from "../components/UebersichtControlZoom.vue"
import UebersichtControlBar from "../components/UebersichtControlBar.vue"
import UebersichtControlFilter from "../components/UebersichtControlFilter.vue"
import UebersichtMarkerSchool from "../components/UebersichtMarkerSchool.vue"
import UebersichtMarkerFocus from "../components/UebersichtMarkerFocus.vue"
import UebersichtMarkerSpace from "../components/UebersichtMarkerSpace.vue"
import UebersichtMarkerUser from "../components/UebersichtMarkerUser.vue"
//import UebersichtPopupUser from "../components/UebersichtPopupUser.vue"

export default {
    name: "Uebersicht",
    components: {
        LMap,
        LTileLayer,
        UebersichtControlProvider,
        UebersichtControlHint,
        UebersichtControlAreas,
        UebersichtPolygonAreas,
        UebersichtControlFilter,
        UebersichtControlZoom,
        UebersichtControlBar,
        UebersichtMarkerSchool,
        UebersichtMarkerFocus,
        UebersichtMarkerSpace,
        UebersichtMarkerUser
    },
    computed: {
        ...mapGetters(['user', 'schulAdresse', 'navHeight']),
        mapMarkerData: function(){
            return this.clusterMarkers(this.angebote, this.zoom)
        },
        // Berechnet aus Standortdaten der Schule die Map-Grenzen für Ansicht
        bounds: function () {
            if (this.schulAdresse)
                return latLngBounds(
                    latLng(this.schulAdresse.bounds.x.lat, this.schulAdresse.bounds.x.lng),
                    latLng(this.schulAdresse.bounds.y.lat, this.schulAdresse.bounds.y.lng)
                )
            else
                return null
        }
    },
    data() {
        return {
            angebote: [],
            filter: 0,
            zoom: 0,
            maxZoom: 18,
            provider: tileProviders[0],
            center: [49.912085, 12.189139],
            attribution: attribution,
            polyVisible: false
        }
    },
    methods: { 
        ...mapActions(['updateSchulAdresse', 'updateUser']),
        showArrows(){
            this.$refs.mp.showArrows()
        },
        // Behandelt Fallback, falls Map-Provider nicht verfügbar ist
        tileErrorHandler(){
            if(!this.provider != tileProviders[2]){
                setAlert(this, "Map-Anbieter nicht verfügbar. Anbieter wurde zu OpenStreetMaps geändert.", "Mitteilung", 'warning')
                this.provider = tileProviders[2]
            }
        },
        handleUserClick (group) {
            if(group.resolveZoom > -1)
                this.$refs.map.mapObject.setView(group, group.resolveZoom)
        },
        // Clustering für Angebote aufrufen
        clusterMarkers(angebote, zoom){
            if(!this.$refs || !this.$refs.map || !this.$refs.map.mapObject)
                return angebote
            
            var threshold = 40 // px maximale empfernung für marker
            var res = [] 
            var found
            if(angebote.length < 2)
                return angebote
            for(var i = 0; i < angebote.length; i++){
                var o1 = angebote[i]
                found = {
                    resolveZoom: -1,
                    lat: o1.lat,
                    lng: o1.lng,
                    latsum: o1.lat,
                    lngsum: o1.lng,
                    angebote: [o1]
                }
                for(var j = i + 1; j < angebote.length; j++){
                    var o2 = angebote[j]
                    if(this.calcDistance(found, o2, zoom) < threshold){
                        found.angebote.push(o2)
                        found.latsum += o2.lat
                        found.lngsum += o2.lng
                        found.lat = found.latsum / found.angebote.length
                        found.lng = found.lngsum / found.angebote.length
                    }
                }
                if(found.angebote.length > 1){
                    res = angebote.filter(r => !found.angebote.some(f => f === r))
                    for(var z = Math.round(zoom)+ 1; z <= 18; z++){
                        var rc = this.clusterMarkers(found.angebote, z)
                        if(rc.length > 1){
                            found.resolveZoom = z
                            break
                        }
                    }
                    res.push(found)
                    return this.clusterMarkers(res, zoom)
                }
            }
            return angebote
        },
        // Berechnet wie weit zwei Objekte mit LatLng auf der Karte in Pixel voneinander entfernt sind
        calcDistance(o1, o2, zoom){
            var p1 = this.$refs.map.mapObject.project(o1, zoom)
            var p2 = this.$refs.map.mapObject.project(o2, zoom)
            return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2))
        },
        // Berechnet Mittelpunkt für Karte
        setCenter(){
            if (this.$route.params.mapUser != undefined)
                this.center = [this.$route.params.mapUser.adresse.lat,this.$route.params.mapUser.adresse.lng]
            else if (this.user == undefined && this.schulAdresse != undefined)
                this.center = [this.schulAdresse.position.lat, this.schulAdresse.position.lng]
            else if (this.user != undefined)
                this.center = [this.user.adresse.lat, this.user.adresse.lng]
        }
    },
    async created(){ // Initialisiert Komponente
        var promiseSchule = undefined, promiseUser = undefined
        if (this.schulAdresse == undefined)
            promiseSchule = this.updateSchulAdresse()
        if (this.user == undefined)
            promiseUser = this.updateUser()

        this.zoom = this.$route.params.mapUser != undefined ? 12 : 10

        try {
            if(promiseSchule)
                await promiseSchule
            if(promiseUser){
                await promiseUser
            }
        }
        catch (err) {
            setAlert(this, err.response.data, 'Laden fehlgeschlagen', 'danger')
        }
        
        this.setCenter()
    },
    mounted() {
        this.$refs.map.mapObject.options.zoomSnap = 0.25
        this.$nextTick(() => window.scrollTo(0, 0))
        // Routing demo funktioniert nicht, weil wir einen Service/Api brauchen
        // this.$nextTick(() => {
        //     L.Routing.control({
        //         waypoints: [
        //             L.latLng(this.user.adresse.lat, this.user.adresse.lng),
        //             L.latLng(this.schulAdresse.lat, this.schulAdresse.lng)
        //         ],
        //         routeWhileDragging: true
        //     }).addTo(this.$refs.map.mapObject)
        // })
    }
}
</script>
<style>
    .leaflet-popup-tip, .leaflet-popup-content-wrapper {
        background-color: #474747
    }
</style>